import { useEffect, useState, useCallback } from 'react';
import { sitesService } from '../../_services/sites.service';

// One fetch of /sites/mine per page, shared through a short session cache so
// the switcher, Works-at chips, Billing block and Locations page don't each
// hit the API. A single-site salon gets `multi=false` and renders nothing.
const KEY = 'ml:sites';
const TTL = 60 * 1000;
let inflight = null;

export function readSitesCache() {
    try {
        const raw = sessionStorage.getItem(KEY);
        if (!raw) return null;
        const c = JSON.parse(raw);
        if (!c || Date.now() - c.at > TTL) return null;
        return c.data;
    } catch (e) { return null; }
}
export function clearSitesCache() { try { sessionStorage.removeItem(KEY); } catch (e) { /* ignore */ } }

export function loadSites(force) {
    const cached = !force && readSitesCache();
    if (cached) return Promise.resolve(cached);
    if (!inflight) {
        inflight = sitesService.mine().then(d => {
            try { sessionStorage.setItem(KEY, JSON.stringify({ at: Date.now(), data: d })); } catch (e) { /* ignore */ }
            inflight = null; return d;
        }).catch(e => { inflight = null; throw e; });
    }
    return inflight;
}

/** Sites of the active site's group that this person can see (none when not in a group). */
export function groupSites(data) {
    const g = data && data.group;
    const ids = new Set(((g && g.salonIds) || []).map(String));
    return ((data && data.sites) || []).filter(s => ids.has(String(s.salonId)));
}
export function inGroup(data) { return groupSites(data).length > 1; }

export default function useSites(opts = {}) {
    const [state, setState] = useState(() => {
        const c = readSitesCache();
        return { loading: !c, data: c, error: null };
    });
    const refresh = useCallback((force = true) => loadSites(force).then(d => setState({ loading: false, data: d, error: null })).catch(e => setState(s => ({ ...s, loading: false, error: e }))), []);
    useEffect(() => {
        if (!localStorage.getItem('accessToken')) return;
        if (!state.data) refresh(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const data = state.data || null;
    const sites = (data && data.sites) || [];
    const active = sites.find(s => s.isActive) || null;
    return {
        loading: state.loading, error: state.error, refresh,
        sites, active, group: data && data.group, activeSalonId: data && data.activeSalonId,
        multi: sites.length > 1,
        // In a GROUP with at least one other site this person can see. Catalogue
        // sync and the Locations control key on this, never on `multi`: a
        // self-employed stylist at two unrelated salons has two sites but no
        // group, and syncing between them is refused by the server.
        inGroup: inGroup(data),
        canAddLocation: !!(data && data.canAddLocation),
        // What another location costs on this owner's plan ({ status, amount, currency, interval }) — null when unknown.
        locationPrice: (data && data.locationPrice) || null,
        isOwnerHere: !!(active && (active.role === 'owner' || active.role === 'manager')),
        // Team > Permissions: a role with `team` at this location (server-resolved).
        canManageTeamHere: !!(data && data.canManageTeam),
    };
}
